import { NotebookCell, ExecutionResult } from '../types';

/** Minimal nbformat v4 structures we read/write. */
interface IpynbOutput {
  output_type: string;
  name?: string;
  text?: string | string[];
  ename?: string;
  evalue?: string;
  traceback?: string[];
  data?: Record<string, string | string[]>;
  metadata?: Record<string, unknown>;
}

interface IpynbCell {
  cell_type: 'code' | 'markdown' | 'raw';
  source: string | string[];
  metadata?: Record<string, unknown>;
  execution_count?: number | null;
  outputs?: IpynbOutput[];
}

export interface JupyterNotebook {
  nbformat: number;
  nbformat_minor: number;
  metadata: Record<string, unknown>;
  cells: IpynbCell[];
}

function sourceToString(source: string | string[] | undefined): string {
  if (!source) return '';
  if (typeof source === 'string') return source;
  return source.join('');
}

function stringToSourceLines(code: string): string[] {
  if (!code) return [];
  // nbformat prefers an array of lines, each ending with \n except possibly the last
  const lines = code.split('\n');
  return lines.map((line, i) => (i < lines.length - 1 ? `${line}\n` : line));
}

function outputText(value: string | string[] | undefined): string {
  if (!value) return '';
  return typeof value === 'string' ? value : value.join('');
}

function executionResultToOutputs(result: ExecutionResult | undefined): IpynbOutput[] {
  if (!result) return [];
  const outputs: IpynbOutput[] = [];

  if (result.stdout) {
    outputs.push({
      output_type: 'stream',
      name: 'stdout',
      text: stringToSourceLines(result.stdout),
    });
  }
  if (result.stderr) {
    outputs.push({
      output_type: 'stream',
      name: 'stderr',
      text: stringToSourceLines(result.stderr),
    });
  }
  if (result.error) {
    outputs.push({
      output_type: 'error',
      ename: 'Error',
      evalue: result.error.split('\n')[0] || 'Error',
      traceback: result.error.split('\n'),
    });
  }
  for (const plot of result.plots || []) {
    // data URL: data:image/png;base64,<payload>
    const base64 = plot.includes(',') ? plot.split(',')[1] : plot.replace(/^data:image\/\w+;base64,/, '');
    outputs.push({
      output_type: 'display_data',
      data: { 'image/png': base64 },
      metadata: {},
    });
  }

  return outputs;
}

function outputsToExecutionResult(outputs: IpynbOutput[] | undefined): ExecutionResult | undefined {
  if (!outputs?.length) return undefined;

  let stdout = '';
  let stderr = '';
  let error: string | undefined;
  const plots: string[] = [];

  for (const out of outputs) {
    if (out.output_type === 'stream') {
      const text = outputText(out.text);
      if (out.name === 'stderr') stderr += text;
      else stdout += text;
    } else if (out.output_type === 'error') {
      error = (out.traceback?.join('\n') || `${out.ename || 'Error'}: ${out.evalue || ''}`).trim();
    } else if (out.output_type === 'display_data' || out.output_type === 'execute_result') {
      const png = out.data?.['image/png'];
      if (png) {
        const raw = typeof png === 'string' ? png : png.join('');
        plots.push(raw.startsWith('data:') ? raw : `data:image/png;base64,${raw}`);
      }
      const textPlain = out.data?.['text/plain'];
      if (textPlain && out.output_type === 'execute_result') {
        stdout += outputText(textPlain);
        if (!stdout.endsWith('\n')) stdout += '\n';
      }
    }
  }

  if (!stdout && !stderr && !error && plots.length === 0) return undefined;

  return {
    stdout,
    stderr,
    error,
    plots,
    executionTimeMs: 0,
    timestamp: new Date().toISOString(),
  };
}

function newCellId(prefix = 'cell') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Convert in-app notebook cells to a Jupyter .ipynb document. */
export function cellsToIpynb(
  cells: NotebookCell[],
  options?: { title?: string }
): JupyterNotebook {
  return {
    nbformat: 4,
    nbformat_minor: 5,
    metadata: {
      kernelspec: {
        display_name: 'Python 3 (Pyodide)',
        language: 'python',
        name: 'python3',
      },
      language_info: {
        name: 'python',
        version: '3.11',
        mimetype: 'text/x-python',
        file_extension: '.py',
      },
      ...(options?.title ? { title: options.title } : {}),
    },
    cells: cells.map((cell) => {
      if (cell.type === 'markdown') {
        return {
          cell_type: 'markdown' as const,
          metadata: {},
          source: stringToSourceLines(cell.code),
        };
      }
      return {
        cell_type: 'code' as const,
        execution_count: cell.executionCount ?? null,
        metadata: {},
        source: stringToSourceLines(cell.code),
        outputs: executionResultToOutputs(cell.output),
      };
    }),
  };
}

/** Parse a Jupyter .ipynb JSON string into in-app notebook cells. */
export function ipynbToCells(json: string): NotebookCell[] {
  let notebook: JupyterNotebook;
  try {
    notebook = JSON.parse(json);
  } catch {
    throw new Error('Invalid .ipynb file: not valid JSON.');
  }

  if (!notebook || !Array.isArray(notebook.cells)) {
    throw new Error('Invalid .ipynb file: missing cells array.');
  }

  const cells: NotebookCell[] = notebook.cells
    .filter((c) => c.cell_type === 'code' || c.cell_type === 'markdown')
    .map((c) => {
      const type = c.cell_type === 'markdown' ? 'markdown' : 'code';
      const code = sourceToString(c.source);
      const cell: NotebookCell = {
        id: newCellId(),
        type,
        code,
        isRunning: false,
      };
      if (type === 'code') {
        if (typeof c.execution_count === 'number') {
          cell.executionCount = c.execution_count;
        }
        cell.output = outputsToExecutionResult(c.outputs);
      }
      return cell;
    });

  if (cells.length === 0) {
    return [{
      id: newCellId(),
      type: 'code',
      code: '# Imported notebook had no code/markdown cells\n',
    }];
  }

  return cells;
}

/** Trigger a browser download of the notebook as an .ipynb file. */
export function downloadIpynb(cells: NotebookCell[], filename: string, title?: string) {
  const notebook = cellsToIpynb(cells, { title });
  const blob = new Blob([JSON.stringify(notebook, null, 2)], {
    type: 'application/x-ipynb+json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = filename.replace(/[^\w.\-]+/g, '_');
  a.href = url;
  a.download = safeName.endsWith('.ipynb') ? safeName : `${safeName}.ipynb`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Read an .ipynb File from an <input type="file"> and return cells. */
export function readIpynbFile(file: File): Promise<NotebookCell[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(ipynbToCells(String(reader.result || '')));
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });
}
